import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Loader } from "components/Loader";
import { designColors, designType } from "services/constants/designTokens";
import { EditCheckInOut } from "./EditCheckInOut";
import { defaultAllSelection } from "services/constants";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export const CardView = ({
  loading,
  filterDepartMemberList,
  page,
  setPage,
  setShiftDetails,
  setOpenSidePeek,
  openSidePeek,
  shiftDetails,
  fetchStatus,
  departmentId,
  setDepartmentApi,
  reCallMemberApi,
}) => {
  const [showIcons, setShowIcons] = useState(true);
  const [editState, setEditState] = useState(false);
  const [intervalTime, setIntervalTime] = useState(200);
  const [rowsPerPage, setLeaveRowsPerPage] = useState(25);
  const [dep_id, setDepId] = useState(defaultAllSelection);
  const [expandedCard, setExpandedCard] = useState(null);

  const handleCardClick = (id) => {
    setExpandedCard(String(expandedCard) === String(id) ? null : id);
  };

  useEffect(() => {
    const recallApi = () => {
      setEditState(false);
      fetchStatus(true);
      reCallMemberApi?.(true);
    };
    if (editState && !openSidePeek) {
      recallApi();
    }
  }, [editState, fetchStatus, openSidePeek, reCallMemberApi]);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowIcons(!showIcons);
      setIntervalTime(200);
    }, intervalTime);
    return () => {
      clearInterval(interval);
    };
  }, [intervalTime, showIcons]);

  const handleChangeLeaveRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setLeaveRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleChangeLeavePage = (event, newPage) => {
    setPage(newPage);
  };

  const OvertimeSection = ({ overtimeDetails }) => {
    if (!overtimeDetails || overtimeDetails.length === 0) return null;

    return (
      <Mui.Box mt={2}>
        {overtimeDetails.map((ot, index) => (
          <Mui.Box key={ot.id || index}>
            <Mui.Grid container spacing={2}>
              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Overtime Start
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {ot.ot_start_formatted || "--"}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Overtime End
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {ot.ot_end_formatted || "--"}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Overtime Hours
                </Mui.Typography>
                <Mui.Typography fontWeight="bold" color="green">
                  {ot.individual_ot_time || "00:00"}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            {index !== overtimeDetails.length - 1 && (
              <Mui.Divider sx={{ mt: 1, mb: 1 }} />
            )}
          </Mui.Box>
        ))}
      </Mui.Box>
    );
  };

  const BreakSection = ({ breakDetails }) => {
    if (!breakDetails || breakDetails.length === 0) return null;

    return (
      <Mui.Box mt={2}>
        {breakDetails.map((brk, index) => (
          <Mui.Box key={brk.id || index}>
            <Mui.Grid container spacing={2}>
              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Break Start
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {brk.break_out_formatted || "--"}
                </Mui.Typography>
              </Mui.Grid>
              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Break End
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {brk.break_in_formatted || "--"}
                </Mui.Typography>
              </Mui.Grid>
              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Break Hours
                </Mui.Typography>
                <Mui.Typography fontWeight="bold" color="#10b981">
                  {brk.individual_break_time || "00:00"}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            {index !== breakDetails.length - 1 && (
              <Mui.Divider sx={{ mt: 1, mb: 1 }} />
            )}
          </Mui.Box>
        ))}
      </Mui.Box>
    );
  };

  const OnDutySection = ({ onDutyDetails }) => {
    if (!onDutyDetails || onDutyDetails.length === 0) return null;

    return (
      <Mui.Box mt={2}>
        {onDutyDetails.map((duty, index) => (
          <Mui.Box key={duty.id || index}>
            <Mui.Grid container spacing={2}>
              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  On Duty Start
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {duty.start_time || "--"}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  On Duty End
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {duty.end_time || "--"}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  On Duty Hours
                </Mui.Typography>
                <Mui.Typography fontWeight="bold" color="#6366f1">
                  {duty.duration || "00:00"}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            {index !== onDutyDetails.length - 1 && (
              <Mui.Divider sx={{ mt: 1, mb: 1 }} />
            )}
          </Mui.Box>
        ))}
      </Mui.Box>
    );
  };

  const PermissionSection = ({ permission_details }) => {
    if (!permission_details || permission_details.length === 0) return null;

    return (
      <Mui.Box mt={2}>
        {permission_details.map((duty, index) => (
          <Mui.Box key={duty.id || index}>
            <Mui.Grid container spacing={2}>
              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Permission Start
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {duty.start_time || "--"}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Permission End
                </Mui.Typography>
                <Mui.Typography fontWeight="bold">
                  {duty.end_time || "--"}
                </Mui.Typography>
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                <Mui.Typography sx={designType.captionText}>
                  Permission Hours
                </Mui.Typography>
                <Mui.Typography fontWeight="bold" color="#6366f1">
                  {duty.duration || "00:00"}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>

            {index !== permission_details.length - 1 && (
              <Mui.Divider sx={{ mt: 1, mb: 1 }} />
            )}
          </Mui.Box>
        ))}
      </Mui.Box>
    );
  };

  return (
    <React.Fragment>
      <Mui.Grid
        container
        spacing={3}
        alignItems="flex-start"
        sx={{ p: 2, justifyContent: "center" }}
      >
        {loading ? (
          <Loader info="Loading..." />
        ) : filterDepartMemberList?.length === 0 ? (
          <Mui.Grid xs={11.5} sx={{ mt: 5 }}>
            <Mui.Alert severity="info">
              {filterDepartMemberList?.length === 0 && !loading
                ? "No data available."
                : "No matching data found for your search."}
            </Mui.Alert>
          </Mui.Grid>
        ) : (
          filterDepartMemberList
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((item) => (
              <Mui.Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Mui.Box
                  onClick={() => handleCardClick(item.id)}
                  sx={{
                    width: "100%",
                    borderRadius: 2,
                    border: "1px solid #E0E0E0",
                    display: "flex",
                    overflow: "visible",
                    boxShadow: "0px 2px 12px rgba(0,0,0,0.12)",
                    backgroundColor: "#fff",
                  }}
                >
                  {/* Left Status Border */}
                  <Mui.Box
                    sx={{
                      width: "6px",
                      backgroundColor: item.status_color,
                    }}
                  />

                  <Mui.Box sx={{ p: 2, width: "100%" }}>
                    {/* Header */}
                    <Mui.Stack direction="row" justifyContent="space-between">
                      <Mui.Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                      >
                        <Mui.Avatar
                          sx={{
                            bgcolor: item.card_display_status_color || item.status_color,
                            width: 36,
                            height: 36,
                          }}
                          alt={item?.user_name?.charAt(0)}
                          src={item?.image_url}
                        />

                        <Mui.Box>
                          <Mui.Typography fontWeight="bold">
                            {item.user_name}
                          </Mui.Typography>
                          <Mui.Typography sx={designType.helperText}>
                            {item.user_code}
                          </Mui.Typography>
                        </Mui.Box>
                      </Mui.Stack>

                      <Mui.Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Mui.Box
                          sx={{
                            px: 2,
                            py: 1,
                            borderRadius: "10px",
                            border: `1px solid ${item.card_display_status_color || item.status_color}`,
                            color: item.card_display_status_color || item.status_color,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: `${item.card_display_status_color || item.status_color}20`,
                          }}
                        >
                          {item.card_display_status || item.type}
                        </Mui.Box>
                        <MoreVert
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            setDepId(item?.department_id);
                            setOpenSidePeek(true);
                            setShiftDetails(item);
                          }}
                        />
                      </Mui.Stack>
                    </Mui.Stack>

                    {/* Dept / Shift / Time */}
                    <Mui.Stack
                      direction="row"
                      spacing={2}
                      mt={1}
                      alignItems="center"
                    >
                      <Mui.Tooltip title={item.department_name}>
                        <Mui.Typography
                          fontSize={13}
                          sx={{
                            flex: 1, // take equal space
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Dept: <b>{item.department_name}</b>
                        </Mui.Typography>
                      </Mui.Tooltip>
                      <Mui.Tooltip title={item.shift_name}>
                        <Mui.Typography
                          fontSize={13}
                          sx={{
                            flex: 1, // take equal space
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Shift: <b>{item.shift_name}</b>
                        </Mui.Typography>
                      </Mui.Tooltip>
                      <Mui.Tooltip title={item.shift_hours}>
                        <Mui.Typography
                          fontSize={13}
                          sx={{
                            width: 120, // fixed size
                            flexShrink: 0, // prevent shrinking
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Duration: <b>{item.shift_hours}</b>
                        </Mui.Typography>
                      </Mui.Tooltip>
                    </Mui.Stack>

                    {/* Check Section */}
                    <Mui.Stack direction="row" spacing={2} mt={2}>
                      <Mui.Box
                        sx={{
                          flex: 1,
                          border: "1px solid #eee",
                          borderRadius: 2,
                          p: 1,
                          textAlign: "center",
                        }}
                      >
                        <Mui.Typography sx={designType.captionText}>
                          Check In
                        </Mui.Typography>

                        <Mui.Typography fontWeight="bold">
                          {item.check_in_time_formatted
                            ? item.check_in_time_formatted
                            : "00:00"}
                        </Mui.Typography>
                        <Mui.Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <LocationOnIcon
                            sx={{ fontSize: 14, color: designColors.slate600 }}
                          />
                          <Mui.Tooltip title={item.checkin_location_name}>
                            <Mui.Typography
                              fontSize={11}
                              color={designColors.slate600}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "60px", // adjust as needed
                              }}
                            >
                              {item.checkin_location_name
                                ? item.checkin_location_name
                                : "N/A"}
                            </Mui.Typography>
                          </Mui.Tooltip>
                        </Mui.Stack>
                      </Mui.Box>

                      <Mui.Box
                        sx={{
                          flex: 1,
                          border: "1px solid #eee",
                          borderRadius: 2,
                          p: 1,
                          textAlign: "center",
                        }}
                      >
                        <Mui.Typography sx={designType.captionText}>
                          Check Out
                        </Mui.Typography>

                        <Mui.Typography fontWeight="bold">
                          {item.check_out_time_formatted
                            ? item.check_out_time_formatted
                            : "00:00"}
                        </Mui.Typography>
                        <Mui.Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <LocationOnIcon
                            sx={{ fontSize: 14, color: designColors.slate600 }}
                          />
                          <Mui.Tooltip title={item?.checkout_location_name}>
                            <Mui.Typography
                              fontSize={11}
                              color={designColors.slate600}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "60px", // adjust as nee
                              }}
                            >
                              {item.checkout_location_name
                                ? item.checkout_location_name
                                : "N/A"}
                            </Mui.Typography>
                          </Mui.Tooltip>
                        </Mui.Stack>
                      </Mui.Box>

                      <Mui.Box
                        sx={{
                          flex: 1,
                          border: "1px solid #eee",
                          borderRadius: 2,
                          p: 1,
                          textAlign: "center",
                        }}
                      >
                        <Mui.Typography sx={designType.captionText}>
                          Worked Hours
                        </Mui.Typography>

                        <Mui.Typography
                          color={item.card_display_status_color || item.status_color}
                          fontWeight="bold"
                        >
                          {item.total_work_time
                            ? item.total_work_time
                            : "00:00"}
                        </Mui.Typography>
                      </Mui.Box>
                    </Mui.Stack>

                    {/* Overtime / Late */}
                    <Mui.Stack
                      direction="row"
                      justifyContent="center"
                      spacing={4}
                      mt={
                        (item.total_over_time &&
                          item.total_over_time !== "00:00") ||
                        (item.late_coming_minutes &&
                          item.late_coming_minutes !== "00:00") ||
                        (item.early_leaving_minutes &&
                          item.early_leaving_minutes !== "00:00") ||
                        item?.permission_details?.length !== 0 ||
                        item?.break_details?.length !== 0 ||
                        item?.onduty_details?.length !== 0 ||
                        item?.overtime_details?.length !== 0
                          ? 1
                          : 3
                      }
                    >
                      {(item?.permission_details?.length !== 0 ||
                        item?.break_details?.length !== 0 ||
                        item?.onduty_details?.length !== 0 ||
                        item?.overtime_details?.length !== 0) && (
                        <Mui.Box>
                          <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />
                        </Mui.Box>
                      )}
                      {item.total_over_time &&
                        item.total_over_time !== "00:00" && (
                          <Mui.Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <TrendingUpIcon
                              sx={{ fontSize: 16, color: "#f59e0b" }}
                            />
                            <Mui.Typography fontSize={12}>
                              Overtime: {item.total_over_time}
                            </Mui.Typography>
                          </Mui.Stack>
                        )}
                      {item.late_coming_minutes &&
                        item.late_coming_minutes !== "00:00" && (
                          <Mui.Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <ScheduleIcon
                              sx={{ fontSize: 16, color: "#ef4444" }}
                            />
                            <Mui.Typography fontSize={12}>
                              Late: {item.late_coming_minutes}
                            </Mui.Typography>
                          </Mui.Stack>
                        )}
                      {item.early_leaving_minutes &&
                        item.early_leaving_minutes !== "00:00" && (
                          <Mui.Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <TrendingDownIcon
                              sx={{ fontSize: 16, color: "#3b82f6" }}
                            />
                            <Mui.Typography fontSize={12}>
                              Early: {item.early_leaving_minutes}
                            </Mui.Typography>
                          </Mui.Stack>
                        )}
                    </Mui.Stack>

                    {/* EXPANDABLE DETAILS */}
                    {/* Break Details */}
                    {String(expandedCard) === String(item.id) &&
                      (item?.permission_details?.length !== 0 ||
                      item?.break_details?.length !== 0 ||
                      item?.onduty_details?.length !== 0 ||
                      item?.overtime_details?.length !== 0 ? (
                        <Mui.Box mt={2}>
                          <Mui.Divider sx={{ mb: 1 }} />
                          {item?.break_details?.length > 0 && (
                            <>
                              <Mui.Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mt: 2 }}
                              >
                                <Mui.Typography fontWeight={600}>
                                  Break Details
                                </Mui.Typography>

                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Typography
                                    fontSize={13}
                                    fontWeight={300}
                                  >
                                    {item.total_break_time || "00:00"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              </Mui.Stack>
                              <BreakSection breakDetails={item.break_details} />
                            </>
                          )}
                          {/* On Duty Details */}
                          {item?.permission_details?.length > 0 && (
                            <>
                              <Mui.Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mt: 2 }}
                              >
                                <Mui.Typography fontWeight={600}>
                                  Permission Details
                                </Mui.Typography>

                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Typography
                                    fontSize={13}
                                    fontWeight={300}
                                  >
                                    {item.total_permission_time || "00:00"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              </Mui.Stack>

                              <PermissionSection
                                permission_details={item?.permission_details}
                              />
                            </>
                          )}

                          {/* On Duty Details */}
                          {item?.onduty_details?.length > 0 && (
                            <>
                              <Mui.Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mt: 2 }}
                              >
                                <Mui.Typography fontWeight={600}>
                                  On Duty Details
                                </Mui.Typography>

                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Typography
                                    fontSize={13}
                                    fontWeight={300}
                                  >
                                    {item.total_on_duty_time || "00:00"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              </Mui.Stack>

                              <OnDutySection
                                onDutyDetails={item?.onduty_details}
                              />
                            </>
                          )}

                          {/* Overtime Details */}
                          {item?.overtime_details?.length > 0 && (
                            <>
                              <Mui.Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mt: 2 }}
                              >
                                <Mui.Typography fontWeight={600}>
                                  Overtime Details
                                </Mui.Typography>

                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Typography
                                    fontSize={13}
                                    fontWeight={300}
                                  >
                                    {item.total_over_time || "00:00"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              </Mui.Stack>

                              <OvertimeSection
                                overtimeDetails={item.overtime_details}
                              />
                            </>
                          )}
                        </Mui.Box>
                      ) : null)}
                  </Mui.Box>
                </Mui.Box>
              </Mui.Grid>
            ))
        )}
      </Mui.Grid>
      {filterDepartMemberList.length > 25 && (
        <Mui.TablePagination
          className="custom-pagination"
          rowsPerPageOptions={[25, 50, 75, 100]}
          component="div"
          count={filterDepartMemberList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangeLeavePage}
          onRowsPerPageChange={handleChangeLeaveRowsPerPage}
        />
      )}

      {openSidePeek ? (
        <EditCheckInOut
          setOpenSidePeek={() => {
            setOpenSidePeek(false);
            setDepartmentApi(true);
          }}
          openSidePeek={openSidePeek}
          shiftDetails={shiftDetails}
          setEditState={setEditState}
          departmentId={dep_id}
        />
      ) : null}
    </React.Fragment>
  );
};
